<template>
  <div data-testid="t-font-picker">
    <content-row>
      <TColorPicker
        :label="$t('forms.font.color')"
        flex
        class="w-full"
        :default-color="color"
        :changed="colorChanged"
        :disabled="disabledColor"
        :colors="colors"
        :template-colors="templateColors"
        :template-colors-label="templateColorsLabel"
        :predefined-colors-label="predefinedColorsLabel"
        data-testid="font-color"
        @color-change="changeColor"
        @get-template-colors="$emit('get-template-colors')"
        @reset="$emit('reset-color')"
      ></TColorPicker>
    </content-row>
    <content-row v-if="!hideFontSize">
      <TDropdown
        ref="dropdownSizeRef"
        :label="$t('forms.font.size')"
        data-testid="font-size"
        flex
        class="w-full"
        with-input
        input-type="number"
        :selected="selectedSize"
        :changed="sizeChanged"
        :disabled="disabledSize"
        :min="0"
        @input-changed="changeSize"
        @reset="$emit('reset-size')"
      >
        <template #selected-content>
          <p style="padding-left: 0px">
            {{ selectedSize ? `${selectedSize}` : "Size" }}
          </p>
        </template>

        <template #content>
          <ul>
            <VueCustomScrollbar
              style="position: relative; margin: auto"
              :style="{ height: calculatedHeightSizes + 'px' }"
              :settings="{ wheelPropagation: false }"
            >
              <li
                v-for="(size, k) in localSizes"
                :key="k"
                style="
                  padding: 4px 0;
                  display: flex;
                  justify-content: flex-start;
                "
                @click="changeSize(size)"
              >
                <p
                  v-if="typeof size === 'number'"
                  style="width: 120px; padding-left: 12px"
                >
                  {{ size }}
                </p>
                <p v-else style="width: 120px; padding-left: 12px">
                  {{ size }}
                </p>
              </li>
            </VueCustomScrollbar>
          </ul>
        </template>
      </TDropdown>
    </content-row>

    <content-row v-if="!hideFontFamily">
      <t-dropdown
        ref="dropdownFontRef"
        :label="$t('forms.font.family')"
        data-testid="font-family"
        flex
        class="w-full"
        rounded-right
        no-border-left
        :changed="fontChanged"
        :disabled="disabledFont"
        @opened="focusQueryInput"
        @closed="closedFonts"
        @reset="$emit('reset-font')"
      >
        <template #selected-content>
          <p
            v-if="selectedFont !== 'Default'"
            style="width: 94px; padding-left: 0px; text-align: left"
            :style="{
              'font-family':
                selectedFont && typeof selectedFont === 'object'
                  ? selectedFont.style
                  : selectedFont,
            }"
          >
            {{
              selectedFont ? getSimpleSelectedFont(selectedFont) : selectFont
            }}
          </p>
          <p v-else style="width: 94px; padding-left: 0px">
            {{ selectedFont }}
          </p>
        </template>

        <template #content>
          <ul>
            <li
              class="border-0 border-b border-solid border-b-gray-400 !bg-gray-600"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16.7812 15.6289C17.0547 15.9023 17.0547 16.3125 16.7812 16.5586C16.6719 16.6953 16.5078 16.75 16.3438 16.75C16.1523 16.75 15.9883 16.6953 15.8516 16.5586L12.1875 12.8945C11.2031 13.6875 9.97266 14.125 8.66016 14.125C5.54297 14.125 3 11.582 3 8.4375C3 5.32031 5.51562 2.75 8.66016 2.75C11.7773 2.75 14.3477 5.32031 14.3477 8.4375C14.3477 9.77734 13.9102 11.0078 13.1172 11.9648L16.7812 15.6289ZM4.3125 8.4375C4.3125 10.8711 6.25391 12.8125 8.6875 12.8125C11.0938 12.8125 13.0625 10.8711 13.0625 8.4375C13.0625 6.03125 11.0938 4.0625 8.6875 4.0625C6.25391 4.0625 4.3125 6.03125 4.3125 8.4375Z"
                  fill="currentColor"
                />
              </svg>
              <input
                ref="queryInput"
                v-model="query"
                :placeholder="searchFont"
                type="text"
                class="rounded-0 box-border block w-full border-none bg-gray-600 p-1 font-inter text-xs leading-[16px] text-gray-50 !outline-none transition duration-300 placeholder:text-gray-100 focus:!text-gray-10"
              />
            </li>
            <VueCustomScrollbar
              ref="fontScrollbar"
              style="position: relative; margin: auto"
              :style="{ height: calculatedHeightFonts + 'px' }"
              :settings="{ wheelPropagation: false }"
            >
              <li
                v-for="(font, k) in fontsToDisplay"
                :key="k"
                ref="font-resf"
                style="
                  padding: 4px 0;
                  display: flex;
                  justify-content: flex-start;
                "
                :class="{
                  '!bg-gray-400': preselectedFont === font,
                  '!bg-gray-600': preselectedFont !== font,
                }"
                @click="changeFont(font)"
                @mouseover="preselectByMoseover(font)"
              >
                <p
                  v-if="font.label !== 'Default'"
                  style="width: 120px; padding-left: 12px"
                  :style="{ 'font-family': font.style }"
                  :class="{
                    '!text-gray-50': preselectedFont === font,
                    '!text-gray-100': preselectedFont !== font,
                  }"
                >
                  {{ font.label }}
                </p>
                <p
                  v-else
                  style="width: 120px; padding-left: 12px"
                  :class="{
                    '!text-gray-50': preselectedFont === font,
                    '!text-gray-100': preselectedFont !== font,
                  }"
                >
                  {{ font.label }}
                </p>
              </li>
            </VueCustomScrollbar>
          </ul>
        </template>
      </t-dropdown>
    </content-row>
  </div>
</template>
<script lang="ts" setup>
  import { useFontStore } from "@/stores/fonts";
  import IFont from "types/IFont/IFont";
  import { TColorPicker, TDropdown } from "@topol/twig-ui";
  import { PerfectScrollbar as VueCustomScrollbar } from "vue3-perfect-scrollbar";
  import {
    ref,
    computed,
    onMounted,
    onBeforeUnmount,
    watch,
    useTemplateRef,
  } from "vue";

  interface Props {
    color?: string;
    selectedSize: number;
    selectedFont?: IFont | string;
    selectFont?: string;
    isResetable?: boolean;
    searchFont?: string;
    colors?: string[];
    templateColors?: string[];
    templateColorsLabel?: string;
    predefinedColorsLabel?: string;
    colorChanged?: boolean;
    sizeChanged?: boolean;
    fontChanged?: boolean;
    hideFontSize?: boolean;
    hideFontFamily?: boolean;
    disabledColor?: boolean;
    disabledSize?: boolean;
    disabledFont?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    color: undefined,
    selectedFont: undefined,
    selectFont: "Select font",
    isResetable: false,
    searchFont: "Search font",
    colors: () => [],
    templateColors: () => [],
    templateColorsLabel: undefined,
    predefinedColorsLabel: undefined,
  });

  const emit = defineEmits([
    "size-changed",
    "font-changed",
    "color-changed",
    "get-template-colors",
    "reset-color",
    "reset-font",
    "reset-size",
  ]);

  const fontStore = useFontStore();

  const dropdownFontRef = useTemplateRef("dropdownFontRef");
  const dropdownSizeRef = useTemplateRef("dropdownSizeRef");
  const query = ref("");
  const queryInput = useTemplateRef("queryInput");
  const refFonts = useTemplateRef("font-resf");
  const fontScrollbar = useTemplateRef("fontScrollbar");
  const keyMove = ref(false);

  const sizes = computed(() => {
    return fontStore.fontSizes;
  });

  const localSizes = computed((): (number | string)[] => {
    if (props.isResetable) {
      return ["Default", ...sizes.value];
    }

    return sizes.value;
  });

  const fonts = computed(() => {
    return fontStore.fonts;
  });

  const localFonts = computed(() => {
    const helpFonts = fonts.value;
    const sortedFonts = helpFonts.sort((a: IFont, b: IFont) => {
      const nameA = a.label.toUpperCase(); // ignore upper and lowercase
      const nameB = b.label.toUpperCase(); // ignore upper and lowercase
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }

      // names must be equal
      return 0;
    });
    if (props.isResetable) {
      return [
        {
          label: "Default",
          value: "",
        },
        ...sortedFonts,
      ] as IFont[];
    }

    return sortedFonts as IFont[];
  });

  const preselectedFont = ref(
    localFonts.value.find((item: IFont) => item.style === props.selectedFont)
  );

  const fontsToDisplay = computed(() => {
    if (query.value) {
      return localFonts.value.filter((font: IFont) =>
        font.label.toLowerCase().includes(query.value.toLowerCase())
      );
    }

    return localFonts.value;
  });

  const calculatedHeightFonts = computed(() => {
    const height = fontsToDisplay.value.length * 40;

    if (height > 207) {
      return 207;
    }

    return height;
  });

  const calculatedHeightSizes = computed(() => {
    const height = sizes.value.length * 40;

    if (height > 240) {
      return 240;
    }

    return height;
  });

  const changeSize = (size: number | string) => {
    emit("size-changed", size);
    if (dropdownSizeRef.value) dropdownSizeRef.value.opened = false;
  };

  const changeFont = (font: IFont) => {
    emit("font-changed", font);
    if (dropdownFontRef.value) dropdownFontRef.value.opened = false;
  };

  const changeColor = (color: string) => {
    emit("color-changed", color);
  };

  const getSimpleSelectedFont = (font: string | IFont) => {
    if (typeof font === "object") {
      return font.label;
    }

    const fontArray = font.split(",");
    return fontArray[0].replace(/['"]+/g, "");
  };

  function focusQueryInput() {
    setTimeout(() => {
      query.value = "";
      if (queryInput.value) queryInput.value.focus();
    }, 10);
  }

  function closedFonts() {
    query.value = "";
    preselectedFont.value = localFonts.value.find(
      (item: IFont) => item.style === props.selectedFont
    );
  }

  function preselectByMoseover(font: IFont) {
    if (keyMove.value) return;
    preselectedFont.value = font;
  }

  function keyEvents(event: KeyboardEvent) {
    if (
      !dropdownFontRef.value ||
      !dropdownFontRef.value.opened ||
      !preselectedFont.value
    )
      return;
    keyMove.value = true;
    if (event.key === "ArrowDown") {
      const index = fontsToDisplay.value.indexOf(preselectedFont.value);
      if (index < fontsToDisplay.value.length - 1) {
        preselectedFont.value = fontsToDisplay.value[index + 1];
        scrollIntoView(index + 1);
      }
    } else if (event.key === "ArrowUp") {
      const index = fontsToDisplay.value.indexOf(preselectedFont.value);
      if (index > 0) {
        preselectedFont.value = fontsToDisplay.value[index - 1];
        scrollIntoView(index - 1);
      }
    } else if (event.key === "Enter") {
      changeFont(preselectedFont.value);
    }
    setTimeout(() => {
      keyMove.value = false;
    }, 50);
  }

  function scrollIntoView(index: number) {
    if (!refFonts.value || !fontScrollbar.value) return;
    const itemBottom =
      refFonts.value[index].offsetTop + refFonts.value[index].offsetHeight;
    const containerBottom =
      fontScrollbar.value.$el.scrollTop + fontScrollbar.value.$el.clientHeight;
    const itemTop = refFonts.value[index].offsetTop;
    const containerTop = fontScrollbar.value.$el.scrollTop;
    if (itemBottom > containerBottom) {
      fontScrollbar.value.$el.scrollTop =
        itemBottom - fontScrollbar.value.$el.clientHeight;
    }
    if (itemTop < containerTop) {
      fontScrollbar.value.$el.scrollTop = itemTop;
    }
  }

  watch(fontsToDisplay, () => {
    if (query.value !== "") {
      preselectedFont.value = fontsToDisplay.value[0];
    }
  });

  onMounted(() => {
    document.addEventListener("keydown", keyEvents);
  });

  onBeforeUnmount(() => {
    document.removeEventListener("keydown", keyEvents);
  });
</script>
<style lang="scss">
  .t-t-font-picker {
    //margin top is comming from TDropdown margin component

    width: 298px;
    border-top-left-radius: var(--b-radius-small);
    border-bottom-left-radius: var(--b-radius-small);
    display: flex;
    align-items: center;

    .color-container {
      height: 38px;
      background-color: var(--c-g600);
      border-left-width: 1px;
      border-top-width: 1px;
      border-bottom-width: 1px;
      border-left-style: solid;
      border-top-style: solid;
      border-bottom-style: solid;
      border-color: var(--c-g500);
      padding-right: 6px;
      padding-left: 6px;
      border-top-left-radius: var(--b-radius-small);
      border-bottom-left-radius: var(--b-radius-small);
      display: flex;
      align-items: center;
    }

    .dropdown-size {
      min-width: 89px;
    }

    .dropdown-font {
      min-width: 166px;
    }

    .color {
      cursor: pointer;
      width: 26px;
      height: 26px;
      border-radius: 100%;
      border: 1px solid var(--c-g400);
      background-color: teal;
    }
  }
</style>
